// src/services/teamService.js
import axiosInstance from 'shared/services/api/axiosInstance';
import { dedupedGet, invalidateGet } from 'shared/services/api/dedupedGet';

class TeamService {
  constructor() {
    // Cache and request tracking
    this.cache = {
      teamData: new Map(),
      subscriptionData: new Map(),
      lastFetch: new Map(),
      pendingRequests: new Map()
    };
    
    // Cache duration in milliseconds (15 minutes - increased to reduce API calls)
    this.CACHE_DURATION = 15 * 60 * 1000;

    // Never let one identity's team/plan survive into another's session in the
    // same tab. Dispatched by axiosInstance's session teardown and by logout.
    if (typeof window !== 'undefined') {
      window.addEventListener('trodo:session-cleared', () => this.clearAll());
    }
    
    // axiosInstance already encapsulates baseURL (/api) and auth handling
  }

  // Helper function to check if cached data is still valid
  isCacheValid(teamId, type) {
    const lastFetch = this.cache.lastFetch.get(`${teamId}-${type}`);
    return lastFetch && (Date.now() - lastFetch) < this.CACHE_DURATION;
  }

  // Helper function to get cached data
  getCachedData(teamId, type) {
    if (this.isCacheValid(teamId, type)) {
      const cache = type === 'team' ? this.cache.teamData : this.cache.subscriptionData;
      return cache.get(teamId);
    }
    return null;
  }

  // Helper function to set cached data
  setCachedData(teamId, type, data) {
    const cache = type === 'team' ? this.cache.teamData : this.cache.subscriptionData;
    cache.set(teamId, data);
    this.cache.lastFetch.set(`${teamId}-${type}`, Date.now());
  }

  /**
   * Drop cached team/billing data.
   *
   * The cache had no way to be invalidated, which is the reason callers avoided
   * it and re-fetched directly — and why a 15-minute TTL was the only thing
   * keeping a stale plan or role from being shown. Any mutation that changes
   * what these endpoints return must call this: upgrading a plan, toggling
   * overage, renaming a team, changing a member's role, accepting an invite.
   *
   * @param {number|string} teamId
   * @param {'team'|'subscription'} [type] omit to drop both
   */
  invalidate(teamId, type) {
    if (teamId == null) return;
    const types = type ? [type] : ['team', 'subscription'];
    for (const t of types) {
      const cache = t === 'team' ? this.cache.teamData : this.cache.subscriptionData;
      cache.delete(teamId);
      this.cache.lastFetch.delete(`${teamId}-${t}`);
      this.cache.pendingRequests.delete(`${t === 'team' ? 'team' : 'subscription'}-${teamId}`);
      if (t === 'subscription') invalidateGet(`/billing/status/${teamId}::`);
    }
  }

  /** Wipe everything — logout, or switching identity. Never leak one user's
   *  team or plan into the next session in the same tab. */
  clearAll() {
    this.cache.teamData.clear();
    this.cache.subscriptionData.clear();
    this.cache.lastFetch.clear();
    this.cache.pendingRequests.clear();
    invalidateGet('/billing/status/');
  }

  /** Create a team; backend uses the JWT user — email is optional/legacy. */
  async createNewTeam(teamName, email = '') {
    const trimmedName = String(teamName || '').trim();
    const trimmedEmail = String(email || '').trim();
    const payload = {
      teamName: trimmedName,
      ...(trimmedEmail ? { email: trimmedEmail } : {}),
      timezone: Intl.DateTimeFormat().resolvedOptions().timeZone
    };
    const response = await axiosInstance.post('/team/createNewTeam', payload);
    const newTeam = response.data?.newTeam;
    if (newTeam) {
      const id = newTeam._id || newTeam.id;
      if (id) this.clearTeamCache(String(id));
    }
    return response.data;
  }

  // Fetch team data with caching and retry logic
  async fetchTeamData(teamId, retryCount = 0) {
    try {
      // Check cache first
      const cachedData = this.getCachedData(teamId, 'team');
      if (cachedData) {
        return cachedData;
      }

      // Check for pending request to avoid duplicates
      const pendingKey = `team-${teamId}`;
      if (this.cache.pendingRequests.has(pendingKey)) {
        return this.cache.pendingRequests.get(pendingKey);
      }

      // Create the promise and store it to prevent duplicate requests
      const requestPromise = axiosInstance.get(`/team/${teamId}`).then(response => {
        if (response.data && response.data._id) {
          this.setCachedData(teamId, 'team', response.data);
          return response.data;
        }
        return null;
      }).catch(error => {
        if (error.response?.status === 429 && retryCount < 3) {
          // Exponential backoff: wait 2^retryCount seconds
          const delay = Math.pow(2, retryCount) * 1000;
          return new Promise(resolve => {
            setTimeout(() => {
              resolve(this.fetchTeamData(teamId, retryCount + 1));
            }, delay);
          });
        }
        console.error('Error fetching team data via service:', error);
        throw error;
      }).finally(() => {
        // Remove from pending requests
        this.cache.pendingRequests.delete(pendingKey);
      });

      this.cache.pendingRequests.set(pendingKey, requestPromise);
      return requestPromise;
    } catch (error) {
      console.error('Error in fetchTeamData service:', error);
      throw error;
    }
  }

  // Fetch subscription data with caching and retry logic
  async fetchSubscriptionData(teamId, retryCount = 0) {
    try {
      // Check cache first
      const cachedData = this.getCachedData(teamId, 'subscription');
      if (cachedData) {
        return cachedData;
      }

      // Check for pending request to avoid duplicates
      const pendingKey = `subscription-${teamId}`;
      if (this.cache.pendingRequests.has(pendingKey)) {
        return this.cache.pendingRequests.get(pendingKey);
      }

      // Create the promise and store it to prevent duplicate requests. Through
      // dedupedGet: stripeService and the auth bootstrap read this same status
      // on load, and this cache cannot see their requests.
      const requestPromise = dedupedGet(`/billing/status/${teamId}`).then(response => {
        // Handle new endpoint response structure
        const subscriptionData = response.data.success ? response.data.data : response.data;
        this.setCachedData(teamId, 'subscription', subscriptionData);
        return subscriptionData;
      }).catch(error => {
        if (error.response?.status === 429 && retryCount < 3) {
          // Exponential backoff: wait 2^retryCount seconds
          const delay = Math.pow(2, retryCount) * 1000;
          return new Promise(resolve => {
            setTimeout(() => {
              resolve(this.fetchSubscriptionData(teamId, retryCount + 1));
            }, delay);
          });
        }
        console.error('Error fetching subscription data via service:', error);
        throw error;
      }).finally(() => {
        // Remove from pending requests
        this.cache.pendingRequests.delete(pendingKey);
      });

      this.cache.pendingRequests.set(pendingKey, requestPromise);
      return requestPromise;
    } catch (error) {
      console.error('Error in fetchSubscriptionData service:', error);
      throw error;
    }
  }

  // Clear cache for a specific team
  clearTeamCache(teamId) {
    this.cache.teamData.delete(teamId);
    this.cache.subscriptionData.delete(teamId);
    this.cache.lastFetch.delete(`${teamId}-team`);
    this.cache.lastFetch.delete(`${teamId}-subscription`);
  }

  // Clear all cache
  clearAllCache() {
    this.cache.teamData.clear();
    this.cache.subscriptionData.clear();
    this.cache.lastFetch.clear();
    this.cache.pendingRequests.clear();
  }
}

// Export a singleton instance
export const teamService = new TeamService();
export default teamService;